/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */
import { LogLevel } from "@azure/msal-browser";

import * as constants from "./constants";

// Create a redirection URL that will be navigated to when the
// user logs in to the system
const currentUrl = window.location.href;
const linkArray = currentUrl.split("/");
const redirectUri = linkArray[0] + "//" + linkArray[2] + "/";

/**
 * Enter here the user flows and custom policies for your B2C application
 * To learn more about user flows, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/user-flow-overview
 * To learn more about custom policies, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/custom-policy-overview
 */
export const b2cPolicies = {
  names: {
    signUpSignIn: constants.SIGN_UP_SIGN_IN_POLICY,
    forgotPassword: constants.FORGOT_PASSWORD_POLICY,
    editProfile: constants.EDIT_PROFILE_POLICY,
  },
  authorities: {
    signUpSignIn: {
      authority: `https://${constants.TENANT_NAME}.b2clogin.com/${constants.TENANT_NAME}.onmicrosoft.com/${constants.SIGN_UP_SIGN_IN_POLICY}`,
    },
    forgotPassword: {
      authority: `https://${constants.TENANT_NAME}.b2clogin.com/${constants.TENANT_NAME}.onmicrosoft.com/${constants.FORGOT_PASSWORD_POLICY}`,
    },
    editProfile: {
      authority: `https://${constants.TENANT_NAME}.b2clogin.com/${constants.TENANT_NAME}.onmicrosoft.com/${constants.EDIT_PROFILE_POLICY}`,
    },
  },
  authorityDomain: `${constants.TENANT_NAME}.b2clogin.com`,
};

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const authConfig = {
  auth: {
    clientId: constants.CLIENT_ID, // This is the ONLY mandatory field that you need to supply.
    authority: b2cPolicies.authorities.signUpSignIn.authority, // Use a sign-up/sign-in user-flow as a default authority
    knownAuthorities: [b2cPolicies.authorityDomain], // Mark your B2C tenant's domain as trusted.
    redirectUri: redirectUri, // Points to window.location.origin. You must register this URI on Azure Portal/App Registration.
    postLogoutRedirectUri: redirectUri, // Indicates the page to navigate after logout.
    navigateToLoginRequestUrl: false, // If "true", will navigate back to the original request location before processing the auth code response.
  },
  cache: {
    cacheLocation: "sessionStorage", // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
    storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        // eslint-disable-next-line default-case
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Info:
            console.info(message);
            return;
          case LogLevel.Verbose:
            console.debug(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
        }
      },
    },
  },
};

/**
 * Add here the endpoints and scopes when obtaining an access token for protected web APIs. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
export const protectedResources = {
  loginApi: {
    endpoint: "http://loacalhost:3000",
    scopes: ["api://uploads-api"], // e.g. api://xxxxxx/access_as_user
  },
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
  scopes: [...protectedResources.loginApi.scopes],
  // extraQueryParameters: { domain_hint: "facebook.com" },
  // extraQueryParameters: { ui_locales: "da" },
};

/**
 * An optional silentRequest object can be used to achieve silent SSO
 * between applications by providing a "login_hint" property.
 */
export const silentRequest = {
  scopes: ["openid", "profile"],
  //   loginHint: "example@domain.net"
};
